/**
 * Restore the server-rendered points block after Horizon variant selection.
 * Liquid remains the only authority for customer tier, rounding, bonus and copy.
 * Keep an empty host for sold-out variants; hide stale content before a request,
 * then adopt only the latest successful response for this product/block/section.
 */
import { StandardEvents } from '@shopify/events';

const TAG = 'ayunel-points-earn';
const shortId = (id) => String(id ?? '').split('/').pop();

class AyunelPointsEarn extends HTMLElement {
  #controller;
  #generation = 0;
  #dirty = false;

  connectedCallback() {
    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.#scope()?.addEventListener(StandardEvents.productSelect, this.#onProductSelect, { signal });
    window.addEventListener('pageshow', this.#verifyAgainstPage, { signal });
    this.#verifyAgainstPage();
  }

  disconnectedCallback() {
    this.#generation += 1;
    this.#controller?.abort();
  }

  #scope() {
    return this.closest('.shopify-section, dialog');
  }

  // Selected controls change before the form's hidden input. Prefer those when
  // the module loads late or a back/forward cache snapshot restores mid-request.
  #pageVariant() {
    const scope = this.#scope();
    if (!scope) return null;
    const rendered = this.dataset.variantId || '';
    const picker = scope.querySelector(`variant-picker[data-product-id="${CSS.escape(this.dataset.productId || '')}"]`);
    const ids = [];
    if (picker) {
      for (const input of picker.querySelectorAll('input[type="radio"]:checked, select')) {
        const option = input.tagName === 'SELECT' ? input.options[input.selectedIndex] : input;
        // An option without a resolvable variant makes the whole combination indeterminate.
        // Never keep the old points merely because another selected radio still carries the old ID.
        if (!option?.dataset.variantId) return null;
        ids.push(String(option.dataset.variantId));
      }
    }
    if (ids.length) {
      if (ids.every((id) => id === ids[0])) return ids[0] || null;
      const changed = ids.filter((id) => id !== rendered);
      return changed.length === 1 ? changed[0] || null : null;
    }
    const input = scope.querySelector('product-form-component input[name="id"], form[action*="/cart/add"] input[name="id"]');
    return input?.value ? String(input.value) : null;
  }

  #verifyAgainstPage = () => {
    const variantId = this.#pageVariant();
    if (variantId && variantId === this.dataset.variantId && !this.#dirty) return;
    const generation = ++this.#generation;
    this.#clear();
    const sectionId = this.dataset.sectionId;
    if (!variantId || !sectionId || !this.dataset.productUrl) return;
    const url = new URL(this.dataset.productUrl, window.location.href);
    url.search = '';
    const view = new URL(window.location.href).searchParams.get('view');
    if (view) url.searchParams.set('view', view);
    url.searchParams.set('variant', variantId);
    url.searchParams.set('section_id', sectionId);
    fetch(url.toString(), { signal: this.#controller.signal })
      .then((response) => {
        if (!response.ok) throw new Error('Points section unavailable');
        return response.text();
      })
      .then((text) => this.#adopt(new DOMParser().parseFromString(text, 'text/html'), generation, variantId))
      .catch(() => { /* Keep hidden until a later valid selection or page restore. */ });
  };

  #clear() {
    this.hidden = true;
    this.replaceChildren();
    this.#dirty = true;
  }

  #adopt(html, generation, variantId) {
    if (generation !== this.#generation) return;
    const next = html.querySelector(`${TAG}[data-block-id="${CSS.escape(this.dataset.blockId || '')}"][data-section-id="${CSS.escape(this.dataset.sectionId || '')}"]`);
    if (!next || next.dataset.productId !== this.dataset.productId || next.dataset.variantId !== variantId) return;
    this.dataset.variantId = next.dataset.variantId;
    this.#dirty = false;
    if (next.hasAttribute('hidden')) return;
    this.replaceChildren(...Array.from(next.childNodes, (node) => document.importNode(node, true)));
    this.hidden = false;
  }

  #onProductSelect = (event) => {
    if (!(event.target instanceof Element)) return;
    if (event.target.closest('product-card') || event.target.closest('quick-add-dialog')) return;
    if (event.target.closest('.shopify-section, dialog') !== this.#scope()) return;
    if (!event.promise || typeof event.promise.then !== 'function') return;
    const productId = shortId(event.product?.id);
    if (productId && productId !== this.dataset.productId) return;
    const generation = ++this.#generation;
    this.#clear();
    event.promise
      .then(({ detail } = {}) => {
        if (!detail?.html || !detail.resource) return;
        if (detail.productId && shortId(detail.productId) !== this.dataset.productId) return;
        this.#adopt(detail.html, generation, shortId(detail.resource.id));
      })
      .catch(() => { /* Failure/abort never restores the previous variant's points. */ });
  };
}

if (!customElements.get(TAG)) customElements.define(TAG, AyunelPointsEarn);
